import Phaser from 'phaser';
import type { Unit as UnitData, NationKey, Factions } from '~shared/types';
import { hexToPixelFlipped, HEX_SIZE } from './HexBoard';

const TOKEN_R = HEX_SIZE * 0.46;  // bounding radius for hit-testing

// ── Figure layout ─────────────────────────────────────────────────────────────
// Side-profile figures face right (+x). [dx, dy] = foot-anchor from hex centre.
// Formation mirrors the original: 2×1, triangle, 2×2 — reformats as figures lost.
const LAYOUTS: [number, number][][] = [
  [],
  [[0, 12]],                                                           // 1
  [[-11, 13], [11, 13]],                                               // 2
  [[0, -3], [-11, 13], [11, 13]],                                      // 3
  [[-11, -3], [11, -3], [-11, 13], [11, 13]],                          // 4
];
const FIG_H = [0, 24, 21, 18, 17];  // figure height (px) per count

// dx/dy = offset from container origin (hex centre) to tank centre.
const ARMOR_LAYOUTS: [number, number][][] = [
  [],
  [[0, 3]],                                          // 1: centred
  [[-18, 2], [18, 2]],                               // 2: side by side (left / right)
  [[0, -14], [-14, 11], [14, 11]],                   // 3: triangle
  [[-14, -10], [14, -10], [-14, 11], [14, 11]],      // 4: 2×2
];

// ── Single infantryman — side profile, facing right (+x = toward enemy) ──────
// Anchored at (bx, by) = foot position. Scale: u = h/20.
// Allies: BAR (Browning Automatic Rifle) — bipod + top box magazine.
// Axis:   MP40 — short barrel, pistol grip, prominent downward curved magazine.

function soldier(
  g: Phaser.GameObjects.Graphics,
  bx: number, by: number, h: number, isAllies: boolean,
): void {
  const u = h / 20;

  const UNIFORM = isAllies ? 0x9a8840 : 0x686860;
  const HELMET  = isAllies ? 0x485020 : 0x2c2c2c;
  const SKIN    = 0xcc9a62;
  const BOOT    = 0x1e1208;
  const WOOD    = 0x5c3412;
  const STEEL   = 0x282018;

  // Ground shadow
  g.fillStyle(0x000000, 0.14);
  g.fillEllipse(bx + 3 * u, by, 10 * u, 1.8 * u);

  // ── Boots ───────────────────────────────────────────────────────────────────
  g.fillStyle(BOOT, 1);
  // Back boot (behind, heel planted)
  g.fillEllipse(bx - 2.5 * u, by - 0.8 * u, 4 * u, 2 * u);
  // Front boot pointing forward (right), toe up slightly — mid-stride
  g.fillPoints([
    { x: bx + 1.5 * u, y: by },
    { x: bx + 6 * u,   y: by - 0.5 * u },
    { x: bx + 6 * u,   y: by - 2 * u },
    { x: bx + 1.5 * u, y: by - 2.2 * u },
    { x: bx + 0.8 * u, y: by - 1.2 * u },
  ], true);

  // ── Legs — wide stride, clearly apart ───────────────────────────────────────
  g.fillStyle(UNIFORM, 0.60);
  g.fillPoints([                                               // back leg angled rearward
    { x: bx - 1 * u,   y: by - 1.5 * u },
    { x: bx + 1.5 * u, y: by - 1.5 * u },
    { x: bx + 0.5 * u, y: by - 10 * u  },
    { x: bx - 2.5 * u, y: by - 10 * u  },
  ], true);
  g.fillStyle(UNIFORM, 1);
  g.fillPoints([                                               // front leg striding forward
    { x: bx + 2 * u,   y: by - 1.8 * u },
    { x: bx + 5 * u,   y: by - 1.8 * u },
    { x: bx + 4.5 * u, y: by - 10 * u  },
    { x: bx + 2 * u,   y: by - 10 * u  },
  ], true);

  // ── Torso (leaning forward into weapon) ─────────────────────────────────────
  g.fillStyle(UNIFORM, 1);
  g.fillPoints([
    { x: bx + 0.3 * u, y: by - 10 * u },
    { x: bx + 3.2 * u, y: by - 10 * u },
    { x: bx + 4.2 * u, y: by - 17 * u },
    { x: bx + 1.5 * u, y: by - 17 * u },
  ], true);

  // ── Weapon ──────────────────────────────────────────────────────────────────
  if (isAllies) {
    // M1 Garand — no external magazine, full wooden stock + handguard, raised receiver
    // Full wooden stock (butt to handguard end, ~2/3 of total length)
    g.fillStyle(WOOD, 1);
    g.fillRect(bx - 0.5 * u, by - 13.8 * u, 8.5 * u, 1.6 * u);
    // Wooden handguard over barrel (slightly narrower strip on top of stock)
    g.fillRect(bx + 4 * u,   by - 15.2 * u, 5 * u,   1.0 * u);
    // Receiver / action — raised hump above stock (distinctive Garand profile)
    g.fillStyle(STEEL, 1);
    g.fillRect(bx + 1.2 * u, by - 15.6 * u, 3.5 * u, 2.0 * u);
    // Thin barrel extending forward past handguard
    g.fillRect(bx + 8.5 * u, by - 14.8 * u, 5.5 * u, 0.9 * u);
    // Trigger guard (small loop below stock — Garand has a prominent one)
    g.fillStyle(STEEL, 0.85);
    g.fillRect(bx + 2 * u, by - 12.8 * u, 2.2 * u, 1.0 * u);
    // Front hand gripping handguard
    g.fillStyle(UNIFORM, 0.9);
    g.fillPoints([
      { x: bx + 5 * u,   y: by - 15.4 * u },
      { x: bx + 6 * u,   y: by - 13.8 * u },
      { x: bx + 8.5 * u, y: by - 13.5 * u },
      { x: bx + 7.5 * u, y: by - 15 * u   },
    ], true);
  } else {
    // MP40 — Maschinenpistole 40
    // Folded stock / stock tube going rearward
    g.fillStyle(STEEL, 1);
    g.fillRect(bx - 1.5 * u, by - 13.5 * u, 4 * u, 1 * u);
    // Receiver body
    g.fillRect(bx + 2 * u, by - 14 * u, 4.5 * u, 1.5 * u);
    // Shorter barrel
    g.fillRect(bx + 6 * u, by - 13.8 * u, 4.5 * u, 1.1 * u);
    // Pistol grip
    g.fillRect(bx + 4 * u, by - 13 * u, 1.4 * u, 2.8 * u);
    // Curved 32-round magazine hanging down (most recognisable MP40 feature)
    g.fillPoints([
      { x: bx + 2.3 * u, y: by - 12.5 * u },
      { x: bx + 4.2 * u, y: by - 12.5 * u },
      { x: bx + 4.6 * u, y: by - 8 * u    },
      { x: bx + 2.7 * u, y: by - 8 * u    },
    ], true);
    // Front arm at pistol grip
    g.fillStyle(UNIFORM, 0.9);
    g.fillPoints([
      { x: bx + 2.5 * u, y: by - 14.5 * u },
      { x: bx + 3.5 * u, y: by - 13.2 * u },
      { x: bx + 5.5 * u, y: by - 13 * u   },
      { x: bx + 4.8 * u, y: by - 14.2 * u },
    ], true);
  }

  // ── Head ────────────────────────────────────────────────────────────────────
  g.fillStyle(SKIN, 1);
  g.fillCircle(bx + 2 * u, by - 19.5 * u, 2.4 * u);

  // ── Helmet ──────────────────────────────────────────────────────────────────
  g.fillStyle(HELMET, 1);
  if (isAllies) {
    // Brodie — very wide flat brim (highly distinctive in profile view)
    g.fillEllipse(bx + 2 * u, by - 19.8 * u, 8.5 * u, 2.2 * u);  // wide brim
    g.fillCircle(bx + 1.8 * u, by - 21.2 * u, 2.3 * u);            // dome
    g.fillStyle(0x6a7c30, 0.30);
    g.fillCircle(bx + 1.2 * u, by - 22 * u, 1 * u);                // sheen
  } else {
    // Stahlhelm — flared neck-guard brim + taller dome (distinctive profile)
    g.fillEllipse(bx + 1.8 * u, by - 19.3 * u, 7.2 * u, 3.2 * u); // flared brim
    g.fillEllipse(bx + 1.8 * u, by - 21 * u,   6 * u,   4 * u);    // taller dome
    g.fillStyle(0x484848, 0.30);
    g.fillCircle(bx + 1.2 * u, by - 22 * u, 0.9 * u);              // sheen
  }
}

// ── Top-down tank sprites ─────────────────────────────────────────────────────
// Sherman (allies) vs Panzer IV (axis), gun pointing toward the top of the hex.

function drawSingleTank(
  g: Phaser.GameObjects.Graphics,
  cx: number, cy: number,
  isAllies: boolean,
  scale: number,
): void {
  const w  = 7.5 * scale;
  const h  = 10.0 * scale;
  const tw = 1.8 * scale;

  const HULL    = isAllies ? 0x526422 : 0x907840;
  const HULL_HI = isAllies ? 0x6a8030 : 0xa89050;  // sloped front armour
  const HULL_SH = isAllies ? 0x3c4c18 : 0x706030;  // engine rear deck
  const TRACK   = 0x181810;
  const TURRET  = isAllies ? 0x3e5018 : 0x685c34;
  const GUN     = isAllies ? 0x2c3812 : 0x4a4028;

  // Shadow
  g.fillStyle(0x000000, 0.18);
  g.fillEllipse(cx + 1.0, cy + 1.0, (w + tw * 2) * 1.1, h * 1.1);

  // Tracks (left + right slabs)
  g.fillStyle(TRACK, 1);
  g.fillRect(cx - w / 2 - tw, cy - h / 2, tw, h);
  g.fillRect(cx + w / 2,       cy - h / 2, tw, h);

  // Track link marks
  g.lineStyle(0.7, 0x2a2818, 0.55);
  for (let i = 1; i <= 4; i++) {
    const ty = cy - h / 2 + h * i / 5;
    g.lineBetween(cx - w / 2 - tw, ty, cx - w / 2, ty);
    g.lineBetween(cx + w / 2,       ty, cx + w / 2 + tw, ty);
  }

  // Drive sprockets (small circles at front/rear of each track)
  g.fillStyle(0x282818, 1);
  for (const ex of [-(w / 2 + tw * 0.5), w / 2 + tw * 0.5]) {
    g.fillCircle(cx + ex, cy - h / 2 + tw * 0.7,  tw * 0.70);
    g.fillCircle(cx + ex, cy + h / 2 - tw * 0.7, tw * 0.70);
  }

  // Hull body
  g.fillStyle(HULL, 1);
  g.fillRect(cx - w / 2, cy - h / 2, w, h);

  // Front sloped armour (trapezoid, brighter)
  g.fillStyle(HULL_HI, 0.80);
  g.fillPoints([
    { x: cx - w / 2,     y: cy - h / 2 },
    { x: cx + w / 2,     y: cy - h / 2 },
    { x: cx + w / 2 - 1, y: cy - h / 2 + h * 0.24 },
    { x: cx - w / 2 + 1, y: cy - h / 2 + h * 0.24 },
  ], true);

  // Engine rear deck (darker)
  g.fillStyle(HULL_SH, 0.75);
  g.fillRect(cx - w / 2 + 0.5, cy + h * 0.30, w - 1, h * 0.20);

  // Hull outline
  g.lineStyle(0.7, 0x080808, 0.85);
  g.strokeRect(cx - w / 2, cy - h / 2, w, h);

  // ── Turret ──────────────────────────────────────────────────────────────────
  const tCY = cy - h * 0.05;  // slightly toward front

  if (isAllies) {
    // Sherman M4: round cast-steel turret
    const tR = 2.8 * scale;
    g.fillStyle(TURRET, 1);
    g.fillCircle(cx, tCY, tR);
    g.lineStyle(0.7, 0x080808, 0.85);
    g.strokeCircle(cx, tCY, tR);

    // Commander's cupola (small circle, offset)
    g.fillStyle(0x4a5820, 1);
    g.fillCircle(cx - tR * 0.30, tCY - tR * 0.30, tR * 0.32);

    // White Allied star on turret
    const sR = tR * 0.50;
    const star = Array.from({ length: 10 }, (_, i) => {
      const a = (i * Math.PI / 5) - Math.PI / 2;
      const r = i % 2 === 0 ? sR : sR * 0.42;
      return { x: cx + r * Math.cos(a), y: tCY + r * Math.sin(a) };
    });
    g.fillStyle(0xffffff, 0.88);
    g.fillPoints(star, true);

    // 75mm gun (shorter barrel, pointing up)
    const gLen = 5.5 * scale;
    g.lineStyle(1.5 * scale, GUN, 1);
    g.lineBetween(cx, tCY - tR, cx, tCY - tR - gLen);
    // Mantlet (rounded gun mount at turret front)
    g.fillStyle(TURRET, 1);
    g.fillEllipse(cx, tCY - tR + 0.5 * scale, tR * 0.70, tR * 0.40);

  } else {
    // Panzer IV: angular box turret + long KwK 40 gun
    const tW = 4.8 * scale;
    const tH = 3.6 * scale;

    g.fillStyle(TURRET, 1);
    g.fillRect(cx - tW / 2, tCY - tH / 2, tW, tH);
    g.lineStyle(0.7, 0x080808, 0.85);
    g.strokeRect(cx - tW / 2, tCY - tH / 2, tW, tH);

    // Commander's hatch (circle on top of turret)
    g.fillStyle(0x5a5030, 1);
    g.fillCircle(cx + tW * 0.15, tCY, tH * 0.26);
    g.lineStyle(0.5, 0x080808, 0.70);
    g.strokeCircle(cx + tW * 0.15, tCY, tH * 0.26);

    // Balkenkreuz (Wehrmacht cross) on hull rear
    const cs = 1.8 * scale;
    const kCY = cy + h * 0.12;
    // Black arms
    g.fillStyle(0x000000, 0.88);
    g.fillRect(cx - cs * 0.30, kCY - cs, cs * 0.60, cs * 2.0);
    g.fillRect(cx - cs,        kCY - cs * 0.30, cs * 2.0, cs * 0.60);
    // White inner cross
    g.fillStyle(0xffffff, 0.72);
    g.fillRect(cx - cs * 0.15, kCY - cs * 0.85, cs * 0.30, cs * 1.70);
    g.fillRect(cx - cs * 0.85, kCY - cs * 0.15, cs * 1.70, cs * 0.30);

    // Long 75mm L/48 gun with muzzle brake
    const gLen = 8.0 * scale;
    g.lineStyle(1.4 * scale, GUN, 1);
    g.lineBetween(cx, tCY - tH / 2, cx, tCY - tH / 2 - gLen);
    // Muzzle brake (wider section near tip)
    g.lineStyle(2.4 * scale, GUN, 1);
    const brakeY = tCY - tH / 2 - gLen + 1.5 * scale;
    g.lineBetween(cx - 1.5 * scale, brakeY, cx + 1.5 * scale, brakeY);
  }
}

// ── Top-down howitzer ─────────────────────────────────────────────────────────
// Barrel points up (toward enemy). Wheels seen edge-on from above = thin vertical
// ellipses. Split V-trail, equilibrator springs, compact breech housing.

function drawSingleCannon(
  g: Phaser.GameObjects.Graphics,
  cx: number, cy: number,
  isAllies: boolean,
  scale: number,
): void {
  const BODY   = isAllies ? 0x5a6828 : 0x806848;
  const DARK   = isAllies ? 0x2e3414 : 0x504030;
  const TIRE   = 0x201c10;
  const SHIELD = isAllies ? 0x4a5a22 : 0x6a5838;
  const STEEL  = isAllies ? 0x546030 : 0x685040;

  const s = scale;

  // Shadow
  g.fillStyle(0x000000, 0.18);
  g.fillEllipse(cx, cy + 1, 18 * s, 22 * s);

  // ── Split trail — V-shape spreading rearward ────────────────────────────
  g.fillStyle(DARK, 1);
  g.fillPoints([
    { x: cx - 1 * s, y: cy + 2 * s },
    { x: cx - 7 * s, y: cy + 13 * s },
    { x: cx - 5 * s, y: cy + 13.5 * s },
    { x: cx,         y: cy + 3 * s },
  ], true);
  g.fillPoints([
    { x: cx + 1 * s, y: cy + 2 * s },
    { x: cx + 7 * s, y: cy + 13 * s },
    { x: cx + 5 * s, y: cy + 13.5 * s },
    { x: cx,         y: cy + 3 * s },
  ], true);
  // Trail spades
  g.fillStyle(STEEL, 0.85);
  g.fillEllipse(cx - 6.5 * s, cy + 13.5 * s, 3 * s, 1.5 * s);
  g.fillEllipse(cx + 6.5 * s, cy + 13.5 * s, 3 * s, 1.5 * s);

  // ── Axle bar ────────────────────────────────────────────────────────────
  const wOffX  = 6.5 * s;
  const wY     = cy + 2 * s;
  g.fillStyle(BODY, 1);
  g.fillRect(cx - wOffX, wY - 1.2 * s, wOffX * 2, 2.4 * s);

  // ── Wheels — seen from above: thin vertical ellipse (tyre edge only) ────
  // The wheel is vertical, axle runs E-W; from above you see the tyre profile:
  // tall (= wheel diameter) and very narrow (= tyre width).
  const tyreDiam  = 9 * s;   // N-S extent = wheel diameter
  const tyreWidth = 2.5 * s; // E-W extent = tyre cross-section width
  for (const wx of [cx - wOffX, cx + wOffX]) {
    g.fillStyle(TIRE, 1);
    g.fillEllipse(wx, wY, tyreWidth, tyreDiam);
    g.lineStyle(0.5, 0x080808, 0.7);
    g.strokeEllipse(wx, wY, tyreWidth, tyreDiam);
    // Hub axle-end stub
    g.fillStyle(STEEL, 1);
    g.fillCircle(wx, wY, 1.0 * s);
  }

  // ── Breech / recoil housing ─────────────────────────────────────────────
  const breechY = cy - 2 * s;
  g.fillStyle(STEEL, 1);
  g.fillEllipse(cx, breechY, 9 * s, 7 * s);
  g.lineStyle(0.9, DARK, 0.85);
  g.strokeEllipse(cx, breechY, 9 * s, 7 * s);
  g.lineStyle(0.6, DARK, 0.35);
  g.lineBetween(cx - 2.2 * s, breechY - 3 * s, cx - 2.2 * s, breechY + 3 * s);
  g.lineBetween(cx + 2.2 * s, breechY - 3 * s, cx + 2.2 * s, breechY + 3 * s);

  // ── Equilibrator springs alongside barrel ───────────────────────────────
  const eqTop = cy - 14 * s;
  const eqBot = cy - 7 * s;
  const eqH   = eqBot - eqTop;
  const eqX   = 2.8 * s;
  g.fillStyle(STEEL, 0.75);
  g.fillRect(cx - eqX - 1.1 * s, eqTop, 2.2 * s, eqH);
  g.fillRect(cx + eqX - 1.1 * s, eqTop, 2.2 * s, eqH);
  g.lineStyle(0.5, DARK, 0.45);
  for (let i = 1; i <= 3; i++) {
    const ly = eqTop + i * eqH / 4;
    g.lineBetween(cx - eqX - 1.1 * s, ly, cx - eqX + 1.1 * s, ly);
    g.lineBetween(cx + eqX - 1.1 * s, ly, cx + eqX + 1.1 * s, ly);
  }

  // ── Gun shield ──────────────────────────────────────────────────────────
  const shY = cy - 8 * s;
  g.fillStyle(SHIELD, 1);
  g.fillRect(cx - 6 * s, shY, 12 * s, 4 * s);
  g.lineStyle(0.8, DARK, 0.9);
  g.strokeRect(cx - 6 * s, shY, 12 * s, 4 * s);
  g.fillStyle(DARK, 0.55);
  g.fillRect(cx - 1 * s, shY + 1.2 * s, 2 * s, 1.5 * s);

  // ── Howitzer barrel — longer than a mortar, wider than a field gun ───────
  const barW   = 3.5 * s;
  const barLen = 12 * s;
  const barTop = shY - barLen;
  g.fillStyle(DARK, 1);
  g.fillRect(cx - barW / 2, barTop, barW, barLen + 1 * s);
  // Muzzle ring
  g.fillStyle(STEEL, 0.88);
  g.fillRect(cx - barW / 2 - 1.5 * s, barTop, barW + 3 * s, 2.5 * s);
  g.lineStyle(0.5, 0x080808, 0.6);
  g.strokeRect(cx - barW / 2, barTop, barW, barLen);
}

function drawArtillery(g: Phaser.GameObjects.Graphics, side: string, figures: number): void {
  const isAllies = side === 'allies';
  const n        = Math.max(1, Math.min(figures, 3));

  if (n === 1) {
    drawSingleCannon(g, 0, 18, isAllies, 1.32);
  } else if (n === 2) {
    drawSingleCannon(g, -9, 18, isAllies, 1.02);
    drawSingleCannon(g,  9, 18, isAllies, 1.02);
  } else {
    drawSingleCannon(g, -10, 13, isAllies, 0.87);
    drawSingleCannon(g,   0, 21, isAllies, 0.87);
    drawSingleCannon(g,  10, 13, isAllies, 0.87);
  }
}

function drawArmor(g: Phaser.GameObjects.Graphics, side: string, figures: number): void {
  const isAllies = side === 'allies';
  const n     = Math.max(1, Math.min(figures, 4));
  const scale = 2.52;

  for (const [dx, dy] of ARMOR_LAYOUTS[n]) {
    drawSingleTank(g, dx, dy, isAllies, scale);
  }
}

// ── Soviet infantryman ────────────────────────────────────────────────────────
// Greatcoat (shinel), SSH-40 helmet (round dome + wide rim), Mosin-Nagant rifle.
function drawSovietSoldier(
  g: Phaser.GameObjects.Graphics, bx: number, by: number, h: number,
): void {
  const u      = h / 20;
  const COAT   = 0x8a7450;   // khaki-brown greatcoat
  const COAT_D = 0x5c4e36;   // shadow/belt
  const HELMET = 0x474720;   // dark olive SSH-40
  const SKIN   = 0xcc9a62;
  const BOOT   = 0x1e1208;
  const WOOD   = 0x4a3010;
  const STEEL  = 0x282018;

  // Shadow
  g.fillStyle(0x000000, 0.14);
  g.fillEllipse(bx + 3 * u, by, 11 * u, 1.8 * u);

  // Boots — partially hidden by long coat
  g.fillStyle(BOOT, 1);
  g.fillEllipse(bx - 2 * u, by - 0.8 * u, 4 * u, 2 * u);
  g.fillPoints([
    { x: bx + 2 * u, y: by },           { x: bx + 6 * u, y: by - 0.5 * u },
    { x: bx + 6 * u, y: by - 2 * u },   { x: bx + 2 * u, y: by - 2.2 * u },
    { x: bx + 1 * u, y: by - 1.2 * u },
  ], true);

  // Greatcoat back leg (wide, reaches almost to boots)
  g.fillStyle(COAT, 0.55);
  g.fillPoints([
    { x: bx - 1.5 * u, y: by - 2.5 * u }, { x: bx + 2 * u, y: by - 2.5 * u },
    { x: bx + 1 * u,   y: by - 11 * u  }, { x: bx - 2.5 * u, y: by - 11 * u },
  ], true);
  // Greatcoat front — wider than US, reaches lower
  g.fillStyle(COAT, 1);
  g.fillPoints([
    { x: bx + 2.5 * u, y: by - 2.5 * u }, { x: bx + 5.5 * u, y: by - 2.5 * u },
    { x: bx + 5 * u,   y: by - 11 * u  }, { x: bx + 2 * u,   y: by - 11 * u  },
  ], true);

  // Torso (wide coat body)
  g.fillStyle(COAT, 1);
  g.fillPoints([
    { x: bx - 2 * u,   y: by - 10.5 * u }, { x: bx + 5.5 * u, y: by - 10.5 * u },
    { x: bx + 5 * u,   y: by - 17 * u   }, { x: bx + 1.5 * u, y: by - 17 * u   },
  ], true);

  // Belt accent
  g.fillStyle(COAT_D, 0.65);
  g.fillRect(bx - 1.5 * u, by - 11.5 * u, 6.5 * u, 1.2 * u);

  // Mosin-Nagant — long bolt-action, wooden stock
  g.fillStyle(WOOD, 1);
  g.fillRect(bx - 0.5 * u, by - 13.8 * u, 8.5 * u, 1.5 * u);   // stock
  g.fillRect(bx + 4 * u,   by - 15.2 * u, 5 * u,   0.9 * u);    // handguard
  g.fillStyle(STEEL, 1);
  g.fillRect(bx + 1.2 * u, by - 15.6 * u, 3.5 * u, 1.9 * u);   // receiver
  g.fillRect(bx + 8.5 * u, by - 14.8 * u, 5.5 * u, 0.8 * u);   // barrel
  // Bayonet (Soviet infantry typically fixed bayonets)
  g.fillStyle(STEEL, 0.8);
  g.fillRect(bx + 14 * u, by - 15.5 * u, 4 * u, 0.5 * u);

  // Head
  g.fillStyle(SKIN, 1);
  g.fillEllipse(bx + 2.5 * u, by - 18.5 * u, 3.8 * u, 3.2 * u);

  // SSH-40 helmet — round dome is the key distinguisher from US/German helmets
  g.fillStyle(HELMET, 1);
  g.fillEllipse(bx + 2.5 * u, by - 20.8 * u, 5.8 * u, 5 * u);  // round dome
  // Wide forward rim
  g.fillPoints([
    { x: bx - 0.5 * u, y: by - 18.5 * u }, { x: bx + 6 * u, y: by - 18.5 * u },
    { x: bx + 5.5 * u, y: by - 17.8 * u }, { x: bx,          y: by - 17.8 * u },
  ], true);
}

// ── Finnish infantryman ───────────────────────────────────────────────────────
// White winter smock, M36 helmet, Suomi KP/-31 SMG (drum magazine).
function drawFinnishSoldier(
  g: Phaser.GameObjects.Graphics, bx: number, by: number, h: number, withSkis = false,
): void {
  const u      = h / 20;
  const SMOCK  = 0xe8e8dc;   // off-white winter smock
  const SMOCK_D = 0xb8b8a8;  // shadow/crease
  const HELMET = 0x484838;   // dark grey-green M36
  const SKIN   = 0xcc9a62;
  const BOOT   = 0x1e1208;
  const STEEL  = 0x282018;

  // Shadow
  g.fillStyle(0x000000, 0.14);
  g.fillEllipse(bx + 3 * u, by, 10 * u, 1.8 * u);

  // Skis — drawn under boots
  if (withSkis) {
    const SKI  = 0xc8a050;  // birch/pine wood
    const BIND = 0x2a1a08;  // dark leather binding
    // Back ski
    g.fillStyle(SKI, 1);
    g.fillRect(bx - 5 * u, by - 1.2 * u, 13 * u, 1 * u);
    g.fillPoints([
      { x: bx + 7.5 * u, y: by - 1.2 * u }, { x: bx + 9.5 * u, y: by - 3.2 * u },
      { x: bx + 8.5 * u, y: by - 3.2 * u }, { x: bx + 7 * u,   y: by - 1.2 * u },
    ], true);
    g.fillStyle(BIND, 0.75);
    g.fillRect(bx + 0.5 * u, by - 1.2 * u, 1.5 * u, 1 * u);
    // Front ski
    g.fillStyle(SKI, 1);
    g.fillRect(bx - 3 * u, by + 0.5 * u, 13 * u, 1 * u);
    g.fillPoints([
      { x: bx + 9.5 * u, y: by + 0.5 * u }, { x: bx + 11.5 * u, y: by - 1.5 * u },
      { x: bx + 10.5 * u, y: by - 1.5 * u }, { x: bx + 9 * u,   y: by + 0.5 * u },
    ], true);
    g.fillStyle(BIND, 0.75);
    g.fillRect(bx + 3 * u, by + 0.5 * u, 1.5 * u, 1 * u);
  }

  // Boots
  g.fillStyle(BOOT, 1);
  g.fillEllipse(bx - 2.5 * u, by - 0.8 * u, 4 * u, 2 * u);
  g.fillPoints([
    { x: bx + 1.5 * u, y: by },           { x: bx + 6 * u, y: by - 0.5 * u },
    { x: bx + 6 * u,   y: by - 2 * u },   { x: bx + 1.5 * u, y: by - 2.2 * u },
    { x: bx + 0.8 * u, y: by - 1.2 * u },
  ], true);

  // Legs — visible below smock
  g.fillStyle(SMOCK_D, 0.55);
  g.fillPoints([
    { x: bx - 1 * u, y: by - 1.5 * u }, { x: bx + 1.5 * u, y: by - 1.5 * u },
    { x: bx + 0.5 * u, y: by - 10 * u }, { x: bx - 2.5 * u, y: by - 10 * u },
  ], true);
  g.fillStyle(SMOCK, 1);
  g.fillPoints([
    { x: bx + 2 * u, y: by - 1.8 * u }, { x: bx + 5 * u, y: by - 1.8 * u },
    { x: bx + 4.5 * u, y: by - 10 * u }, { x: bx + 2 * u, y: by - 10 * u },
  ], true);

  // White smock torso — looser/puffier than regular uniform
  g.fillStyle(SMOCK, 1);
  g.fillPoints([
    { x: bx - 1.5 * u, y: by - 10 * u }, { x: bx + 5.5 * u, y: by - 10 * u },
    { x: bx + 5 * u,   y: by - 17 * u }, { x: bx + 1.2 * u, y: by - 17 * u },
  ], true);
  // Smock crease line
  g.fillStyle(SMOCK_D, 0.4);
  g.fillRect(bx + 1.8 * u, by - 16.5 * u, 0.8 * u, 6 * u);

  // Suomi KP/-31 — compact SMG with distinctive round drum magazine below
  g.fillStyle(STEEL, 1);
  g.fillRect(bx, by - 14 * u, 6.5 * u, 1.4 * u);        // receiver
  g.fillRect(bx + 6.5 * u, by - 14.3 * u, 6 * u, 0.8 * u); // barrel (short)
  // Drum magazine below receiver
  g.fillStyle(STEEL, 0.85);
  g.fillCircle(bx + 2.8 * u, by - 12.2 * u, 2.2 * u);   // drum mag
  // Grip
  g.fillStyle(STEEL, 0.7);
  g.fillRect(bx + 1 * u, by - 14 * u, 1 * u, 3 * u);

  // Head
  g.fillStyle(SKIN, 1);
  g.fillEllipse(bx + 2.5 * u, by - 18.5 * u, 3.8 * u, 3.2 * u);

  // M36 helmet — similar round profile but with narrower brim than SSH
  g.fillStyle(HELMET, 1);
  g.fillEllipse(bx + 2.5 * u, by - 20.5 * u, 5.5 * u, 4.8 * u);  // dome
  // Narrow rim
  g.fillPoints([
    { x: bx,           y: by - 18.5 * u }, { x: bx + 5.5 * u, y: by - 18.5 * u },
    { x: bx + 5 * u,   y: by - 17.9 * u }, { x: bx + 0.5 * u, y: by - 17.9 * u },
  ], true);
}

// ── British infantryman ───────────────────────────────────────────────────────
// Brodie helmet (wide flat brim), Sten gun, khaki battledress.
function drawBritishSoldier(
  g: Phaser.GameObjects.Graphics, bx: number, by: number, h: number,
): void {
  const u      = h / 20;
  const KHAKI  = 0x7a7840;   // British khaki battledress
  const HELMET = 0x484a20;   // dark olive Brodie
  const SKIN   = 0xcc9a62;
  const BOOT   = 0x1e1208;
  const WOOD   = 0x5c3412;
  const STEEL  = 0x282018;

  // Shadow
  g.fillStyle(0x000000, 0.14);
  g.fillEllipse(bx + 3 * u, by, 10 * u, 1.8 * u);

  // Boots
  g.fillStyle(BOOT, 1);
  g.fillEllipse(bx - 2.5 * u, by - 0.8 * u, 4 * u, 2 * u);
  g.fillPoints([
    { x: bx + 1.5 * u, y: by },           { x: bx + 6 * u, y: by - 0.5 * u },
    { x: bx + 6 * u,   y: by - 2 * u },   { x: bx + 1.5 * u, y: by - 2.2 * u },
    { x: bx + 0.8 * u, y: by - 1.2 * u },
  ], true);

  // Legs
  g.fillStyle(KHAKI, 0.60);
  g.fillPoints([
    { x: bx - 1 * u, y: by - 1.5 * u }, { x: bx + 1.5 * u, y: by - 1.5 * u },
    { x: bx + 0.5 * u, y: by - 10 * u }, { x: bx - 2.5 * u, y: by - 10 * u },
  ], true);
  g.fillStyle(KHAKI, 1);
  g.fillPoints([
    { x: bx + 2 * u, y: by - 1.8 * u }, { x: bx + 5 * u, y: by - 1.8 * u },
    { x: bx + 4.5 * u, y: by - 10 * u }, { x: bx + 2 * u, y: by - 10 * u },
  ], true);

  // Torso
  g.fillStyle(KHAKI, 1);
  g.fillPoints([
    { x: bx + 0.3 * u, y: by - 10 * u }, { x: bx + 3.2 * u, y: by - 10 * u },
    { x: bx + 4.2 * u, y: by - 17 * u }, { x: bx + 1.5 * u, y: by - 17 * u },
  ], true);

  // Sten gun — very simple tubular SMG, short with side-loading magazine
  g.fillStyle(STEEL, 1);
  g.fillRect(bx, by - 14.2 * u, 7 * u, 1.2 * u);           // receiver tube
  g.fillRect(bx + 7 * u, by - 14.4 * u, 7 * u, 0.7 * u);   // barrel
  // Side magazine (distinctive horizontal stick mag)
  g.fillRect(bx + 2 * u, by - 16.2 * u, 3.5 * u, 2 * u);
  // Grip
  g.fillStyle(WOOD, 1);
  g.fillRect(bx + 0.5 * u, by - 14.2 * u, 1.2 * u, 3 * u);

  // Head
  g.fillStyle(SKIN, 1);
  g.fillEllipse(bx + 2.5 * u, by - 18.5 * u, 3.8 * u, 3.2 * u);

  // Brodie helmet — wide flat brim, shallow dome; very distinctive silhouette
  g.fillStyle(HELMET, 1);
  g.fillEllipse(bx + 2.5 * u, by - 20 * u, 5 * u, 3.5 * u);  // shallow dome
  // Wide flat brim (goes well beyond head on both sides)
  g.fillEllipse(bx + 2.5 * u, by - 18.5 * u, 8 * u, 1.8 * u);
}

// ── Soviet / Finnish T-26 light tank ─────────────────────────────────────────
// Compact 1930s hull, small round single-man turret, 45mm gun.
function drawT26(
  g: Phaser.GameObjects.Graphics, cx: number, cy: number, isFinnish: boolean, scale: number,
): void {
  const HULL    = isFinnish ? 0x566040 : 0x3e4e22;  // Finnish grey-green / Soviet dark green
  const HULL_HI = isFinnish ? 0x6a7450 : 0x4e6030;
  const HULL_SH = isFinnish ? 0x404830 : 0x2c3818;
  const TURRET  = isFinnish ? 0x4e5838 : 0x374420;
  const GUN     = 0x1a1a14;

  const w  = 13 * scale;
  const h  = 16 * scale;
  const tw = 2.5 * scale;

  // Tracks — narrow ellipses inside the hull width
  g.fillStyle(0x1a1810, 1);
  for (const ex of [-(w / 2 + tw * 0.5), w / 2 + tw * 0.5]) {
    g.fillCircle(cx + ex, cy - h / 2 + tw * 0.7, tw * 0.65);
    g.fillCircle(cx + ex, cy + h / 2 - tw * 0.7, tw * 0.65);
    g.fillRect(cx + ex - tw * 0.65, cy - h / 2 + tw * 0.7, tw * 1.3, h - tw * 1.4);
  }

  // Hull body — shorter and narrower than Sherman/PanzerIV
  g.fillStyle(HULL, 1);
  g.fillRect(cx - w / 2, cy - h / 2, w, h);
  g.fillStyle(HULL_HI, 0.75);
  g.fillPoints([
    { x: cx - w / 2, y: cy - h / 2 }, { x: cx + w / 2, y: cy - h / 2 },
    { x: cx + w / 2, y: cy - h / 2 + h * 0.22 }, { x: cx - w / 2, y: cy - h / 2 + h * 0.22 },
  ], true);
  g.fillStyle(HULL_SH, 0.7);
  g.fillRect(cx - w / 2 + 0.5, cy + h * 0.28, w - 1, h * 0.22);
  g.lineStyle(0.7, 0x080808, 0.85);
  g.strokeRect(cx - w / 2, cy - h / 2, w, h);

  // Small round turret (single-man — much smaller than Sherman)
  const tCY = cy - h * 0.08;
  const tR  = 2.2 * scale;
  g.fillStyle(TURRET, 1);
  g.fillCircle(cx, tCY, tR);
  g.lineStyle(0.7, 0x080808, 0.85);
  g.strokeCircle(cx, tCY, tR);

  // National marking on turret
  if (isFinnish) {
    // Finnish blue roundel
    g.fillStyle(0x1a4488, 0.85);
    g.fillCircle(cx, tCY, tR * 0.55);
    g.fillStyle(0xffffff, 0.75);
    g.fillCircle(cx, tCY, tR * 0.28);
  } else {
    // Soviet red star
    const sR = tR * 0.55;
    const star = Array.from({ length: 10 }, (_, i) => {
      const a = (i * Math.PI / 5) - Math.PI / 2;
      const r = i % 2 === 0 ? sR : sR * 0.42;
      return { x: cx + r * Math.cos(a), y: tCY + r * Math.sin(a) };
    });
    g.fillStyle(0xcc1515, 0.9);
    g.fillPoints(star, true);
  }

  // 45mm gun — shorter than Sherman, longer than PanzerIV proportionally
  const gLen = 6.5 * scale;
  g.lineStyle(1.2 * scale, GUN, 1);
  g.lineBetween(cx, tCY - tR, cx, tCY - tR - gLen);
}

// ── French Resistance fighter ─────────────────────────────────────────────────
// Dark civilian clothes, beret (the iconic silhouette), Sten gun.
function drawFrenchResistanceSoldier(
  g: Phaser.GameObjects.Graphics, bx: number, by: number, h: number,
): void {
  const u      = h / 20;
  const JACKET  = 0x28303e;   // dark navy civilian jacket
  const TROUS   = 0x1c2230;   // very dark navy trousers
  const BERET   = 0x18181e;   // near-black beret
  const LAPEL   = 0x38404e;   // lighter lapel/collar accent
  const SKIN    = 0xcc9a62;
  const SHOE    = 0x160e06;   // dark leather civilian shoe
  const STEEL   = 0x282018;
  const WOOD    = 0x4a3010;

  // Shadow
  g.fillStyle(0x000000, 0.14);
  g.fillEllipse(bx + 3 * u, by, 10 * u, 1.8 * u);

  // Shoes — lighter profile than military boots
  g.fillStyle(SHOE, 1);
  g.fillEllipse(bx - 2.5 * u, by - 0.6 * u, 3.5 * u, 1.5 * u);
  g.fillPoints([
    { x: bx + 1.5 * u, y: by },           { x: bx + 5.5 * u, y: by - 0.3 * u },
    { x: bx + 5.5 * u, y: by - 1.6 * u }, { x: bx + 1.5 * u, y: by - 1.8 * u },
    { x: bx + 0.8 * u, y: by - 1 * u },
  ], true);

  // Legs — dark civilian trousers, narrower cut than uniform trousers
  g.fillStyle(TROUS, 0.65);
  g.fillPoints([
    { x: bx - 0.8 * u, y: by - 1.5 * u }, { x: bx + 1.5 * u, y: by - 1.5 * u },
    { x: bx + 0.5 * u, y: by - 10 * u  }, { x: bx - 2 * u,   y: by - 10 * u  },
  ], true);
  g.fillStyle(TROUS, 1);
  g.fillPoints([
    { x: bx + 2 * u,   y: by - 1.8 * u }, { x: bx + 5 * u, y: by - 1.8 * u },
    { x: bx + 4.5 * u, y: by - 10 * u  }, { x: bx + 2 * u, y: by - 10 * u  },
  ], true);

  // Jacket torso
  g.fillStyle(JACKET, 1);
  g.fillPoints([
    { x: bx + 0.2 * u, y: by - 10 * u }, { x: bx + 3.5 * u, y: by - 10 * u },
    { x: bx + 4.3 * u, y: by - 17 * u }, { x: bx + 1.2 * u, y: by - 17 * u },
  ], true);
  // Open lapel — civilian jacket reads differently from a military tunic
  g.fillStyle(LAPEL, 0.6);
  g.fillPoints([
    { x: bx + 2 * u,   y: by - 17 * u  }, { x: bx + 3.8 * u, y: by - 14.5 * u },
    { x: bx + 3 * u,   y: by - 11 * u  }, { x: bx + 1.8 * u, y: by - 11 * u  },
  ], true);

  // Sten gun — the Resistance's signature British-supplied SMG
  g.fillStyle(STEEL, 1);
  g.fillRect(bx,         by - 14.2 * u, 7 * u, 1.2 * u);  // receiver tube
  g.fillRect(bx + 7 * u, by - 14.4 * u, 7 * u, 0.7 * u);  // barrel
  // Horizontal side magazine (most recognisable Sten feature)
  g.fillRect(bx + 2 * u, by - 16.2 * u, 3.5 * u, 2 * u);
  g.fillStyle(WOOD, 1);
  g.fillRect(bx + 0.5 * u, by - 14.2 * u, 1.2 * u, 3 * u);  // grip

  // Head
  g.fillStyle(SKIN, 1);
  g.fillEllipse(bx + 2.5 * u, by - 18.5 * u, 3.8 * u, 3.2 * u);

  // Beret — wide, flat, droops to the left (back of head)
  // The asymmetric droop to the left is THE visual signature of the beret.
  g.fillStyle(BERET, 1);
  // Main beret body — broad ellipse sitting on the head
  g.fillPoints([
    { x: bx - 2.5 * u, y: by - 20.8 * u },  // left droop (lower)
    { x: bx + 0.5 * u, y: by - 22.4 * u },  // top-left peak
    { x: bx + 4.5 * u, y: by - 22.2 * u },  // top-right
    { x: bx + 5.8 * u, y: by - 21 * u   },  // right edge (stays near head)
    { x: bx + 5 * u,   y: by - 20 * u   },  // lower-right (headband level)
    { x: bx - 0.5 * u, y: by - 20 * u   },  // lower-left (headband level)
  ], true);
  // Headband — tight strip where beret meets forehead
  g.fillStyle(0x0a0a10, 0.85);
  g.fillRect(bx + 0.5 * u, by - 20.4 * u, 4.8 * u, 0.8 * u);
  // Small pip/button on top-centre of beret
  g.fillStyle(BERET, 0.5);
  g.fillCircle(bx + 2.2 * u, by - 22.3 * u, 0.5 * u);
}

// ── Nation-aware dispatch functions ───────────────────────────────────────────

function drawInfantryByNation(
  g: Phaser.GameObjects.Graphics, nation: NationKey, n: number, facesSouth: boolean, variant?: string,
): void {
  const layout   = LAYOUTS[n];
  const h        = FIG_H[1];
  const withSkis = nation === 'finnish' && variant === 'ski-troops';
  for (const [dx, dy] of layout) {
    if (nation === 'soviet')       drawSovietSoldier(g, dx, dy, h);
    else if (nation === 'finnish') drawFinnishSoldier(g, dx, dy, h, withSkis);
    else if (nation === 'british') drawBritishSoldier(g, dx, dy, h);
    else if (nation === 'french')  drawFrenchResistanceSoldier(g, dx, dy, h);
    else                           soldier(g, dx, dy, h, nation !== 'german');
  }
  g.setAngle(0);
  g.setScale(facesSouth ? -1 : 1, 1);
}

function drawArmorByNation(
  g: Phaser.GameObjects.Graphics, nation: NationKey, n: number,
): void {
  const count = Math.max(1, Math.min(n, 4));
  const scale = 2.52;
  if (nation === 'soviet' || nation === 'finnish') {
    for (const [dx, dy] of ARMOR_LAYOUTS[count]) {
      drawT26(g, dx, dy, nation === 'finnish', scale * 0.75);
    }
  } else {
    const isAllies = nation !== 'german';
    for (const [dx, dy] of ARMOR_LAYOUTS[count]) {
      drawSingleTank(g, dx, dy, isAllies, scale);
    }
  }
  g.setScale(1, 1);
}

function drawArtilleryByNation(
  g: Phaser.GameObjects.Graphics, nation: NationKey, figures: number,
): void {
  // Artillery shape is similar across nations — colour varies via isAllies flag
  const isAllies = nation !== 'german' && nation !== 'finnish';
  const n = Math.max(1, Math.min(figures, 3));
  if (n === 1) {
    drawSingleCannon(g, 0, 18, isAllies, 1.32);
  } else if (n === 2) {
    drawSingleCannon(g, -9, 18, isAllies, 1.02);
    drawSingleCannon(g,  9, 18, isAllies, 1.02);
  } else {
    drawSingleCannon(g, -10, 13, isAllies, 0.87);
    drawSingleCannon(g,   0, 21, isAllies, 0.87);
    drawSingleCannon(g,  10, 13, isAllies, 0.87);
  }
}

// ── Variant badges ────────────────────────────────────────────────────────────

// British Commando: red heraldic shield with a white flying-Pegasus silhouette.
function drawCommandoBadge(g: Phaser.GameObjects.Graphics, bx: number, by: number): void {
  const W = 9, H = 11;   // half-width / half-height of shield

  // ── Shield body ──────────────────────────────────────────────────────────
  const shield = [
    { x: bx - W,     y: by - H * 0.50 },   // top-left
    { x: bx + W,     y: by - H * 0.50 },   // top-right
    { x: bx + W,     y: by + H * 0.20 },   // lower-right
    { x: bx,         y: by + H * 0.56 },   // bottom point
    { x: bx - W,     y: by + H * 0.20 },   // lower-left
  ];
  g.fillStyle(0xcc1515, 1);
  g.fillPoints(shield, true);
  g.lineStyle(1.2, 0x550000, 1);
  g.strokePoints(shield, true);

  // ── White Pegasus silhouette ──────────────────────────────────────────────
  g.fillStyle(0xffffff, 0.94);

  // Torso (horse body, slightly angled nose-up)
  g.fillEllipse(bx - 0.5, by + 1.5, 9.5, 4.2);

  // Left wing — sweeps up and to the left
  g.fillPoints([
    { x: bx - 1,   y: by + 0.5  },
    { x: bx - W + 1.5, y: by - H * 0.44 + 1 },
    { x: bx - W + 3,   y: by - H * 0.28 + 1 },
    { x: bx - 3,   y: by + 1.5  },
  ], true);

  // Right wing — sweeps up and to the right
  g.fillPoints([
    { x: bx + 1,   y: by      },
    { x: bx + W - 1.5, y: by - H * 0.46 + 1 },
    { x: bx + W - 2.5, y: by - H * 0.28 + 1 },
    { x: bx + 2,   y: by + 1  },
  ], true);

  // Neck
  g.fillEllipse(bx + 4.5, by - 0.5, 3.2, 5);

  // Head (faces right)
  g.fillCircle(bx + 6, by - 2.8, 2.5);

  // Ear (small triangle above head)
  g.fillPoints([
    { x: bx + 5.2, y: by - 5.0 },
    { x: bx + 6.5, y: by - 5.4 },
    { x: bx + 6.8, y: by - 4.0 },
  ], true);

  // Foreleg (right-front) — just a short stroke at this scale
  g.lineStyle(1.4, 0xffffff, 0.88);
  g.lineBetween(bx + 3.5, by + 3, bx + 4.5, by + 5.5);
}

// British Airborne: maroon shield (the airborne beret colour) with a white parachute.
function drawAirborneBadge(g: Phaser.GameObjects.Graphics, bx: number, by: number): void {
  const W = 9, H = 11;

  const shield = [
    { x: bx - W, y: by - H * 0.50 },
    { x: bx + W, y: by - H * 0.50 },
    { x: bx + W, y: by + H * 0.20 },
    { x: bx,     y: by + H * 0.56 },
    { x: bx - W, y: by + H * 0.20 },
  ];
  g.fillStyle(0x7a0000, 1);
  g.fillPoints(shield, true);
  g.lineStyle(1.2, 0x350000, 1);
  g.strokePoints(shield, true);

  // Parachute canopy — flattened dome from left edge to right edge, top of shield interior
  const cX = bx, cY = by - 0.5, cR = 5.5, cRy = 3.8;
  const dome: { x: number; y: number }[] = [];
  for (let a = Math.PI; a >= 0; a -= Math.PI / 12) {
    dome.push({ x: cX + Math.cos(a) * cR, y: cY - Math.sin(a) * cRy });
  }
  g.fillStyle(0xffffff, 0.92);
  g.fillPoints(dome, true);

  // Suspension lines — 5 lines from dome base to harness
  const hY = by + 4.5;
  g.lineStyle(0.9, 0xffffff, 0.85);
  for (let i = 0; i <= 4; i++) {
    const lx = cX - cR + (2 * cR * i / 4);
    g.lineBetween(lx, cY, bx, hY);
  }

  // Harness / soldier silhouette (small white cross shape)
  g.fillStyle(0xffffff, 0.90);
  g.fillRect(bx - 1.2, hY,       2.4, 3.5);  // body
  g.fillRect(bx - 2.8, hY + 0.8, 5.6, 1.5);  // arms
}

// US Rangers: orange/gold heraldic shield with the deep-indigo "Rangers" diamond
// at its centre (the historic 2nd/5th Ranger Battalion diamond patch — the
// "RANGERS" lettering is far too small to render at this scale, so the badge
// captures the shield + diamond silhouette with a thin red accent on the diamond).
function drawRangerBadge(g: Phaser.GameObjects.Graphics, bx: number, by: number): void {
  const W = 9, H = 11;   // half-width / half-height of shield

  // ── Shield body ──────────────────────────────────────────────────────────
  const shield = [
    { x: bx - W,     y: by - H * 0.50 },   // top-left
    { x: bx + W,     y: by - H * 0.50 },   // top-right
    { x: bx + W,     y: by + H * 0.20 },   // lower-right
    { x: bx,         y: by + H * 0.56 },   // bottom point
    { x: bx - W,     y: by + H * 0.20 },   // lower-left
  ];
  g.fillStyle(0xd9943f, 1);
  g.fillPoints(shield, true);
  g.lineStyle(1.2, 0x7a4a18, 1);
  g.strokePoints(shield, true);

  // ── Central diamond (the "Rangers" patch) ────────────────────────────────
  const dW = 5.4, dH = 6.4;
  const cy = by + 0.5;
  const diamond = [
    { x: bx,       y: cy - dH * 0.5 },   // top
    { x: bx + dW,  y: cy            },   // right
    { x: bx,       y: cy + dH * 0.5 },   // bottom
    { x: bx - dW,  y: cy            },   // left
  ];
  g.fillStyle(0x382f6e, 1);
  g.fillPoints(diamond, true);
  g.lineStyle(0.9, 0xc23b3b, 0.95);
  g.strokePoints(diamond, true);
}

// French Resistance: shield in the French tricolour (blue / white / red vertical
// bands) bearing a red Cross of Lorraine — the historic emblem of Free France
// and the Resistance.
function drawResistanceBadge(g: Phaser.GameObjects.Graphics, bx: number, by: number): void {
  const W = 9, H = 11;   // half-width / half-height of shield

  const shield = [
    { x: bx - W,     y: by - H * 0.50 },   // top-left
    { x: bx + W,     y: by - H * 0.50 },   // top-right
    { x: bx + W,     y: by + H * 0.20 },   // lower-right
    { x: bx,         y: by + H * 0.56 },   // bottom point
    { x: bx - W,     y: by + H * 0.20 },   // lower-left
  ];

  // ── Tricolour bands (blue / white / red) ─────────────────────────────────
  const topY     = by - H * 0.50;
  const midY     = by + H * 0.20;
  const stripeW  = (2 * W) / 3;

  // White base covers the whole shield (the bottom point sits in the centre
  // "white" band).
  g.fillStyle(0xf5f0e6, 1);
  g.fillPoints(shield, true);

  // Blue left band / red right band — the rectangular upper portion only.
  g.fillStyle(0x1f3a8f, 1);
  g.fillRect(bx - W, topY, stripeW, midY - topY);
  g.fillStyle(0xb5252b, 1);
  g.fillRect(bx + W - stripeW, topY, stripeW, midY - topY);

  // ── Cross of Lorraine ─────────────────────────────────────────────────────
  g.fillStyle(0xb5252b, 1);
  g.fillRect(bx - 1, by - H * 0.42, 2, H * 0.78);   // vertical bar
  g.fillRect(bx - 6, by - H * 0.05, 12, 1.6);       // lower (longer) crossbar
  g.fillRect(bx - 3, by - H * 0.30, 6, 1.4);        // upper (shorter) crossbar

  // ── Shield outline ────────────────────────────────────────────────────────
  g.lineStyle(1.2, 0x8a6a1f, 1);
  g.strokePoints(shield, true);
}

// Finnish Ski Troops: red shield bearing the Finnish heraldic lion — a golden
// lion rampant wielding a sword, symbol of Finland since the 13th century.
function drawSkiTroopsBadge(g: Phaser.GameObjects.Graphics, bx: number, by: number): void {
  const W = 9, H = 11;

  // Red shield
  const shield = [
    { x: bx - W, y: by - H * 0.50 },
    { x: bx + W, y: by - H * 0.50 },
    { x: bx + W, y: by + H * 0.20 },
    { x: bx,     y: by + H * 0.56 },
    { x: bx - W, y: by + H * 0.20 },
  ];
  g.fillStyle(0xcc1515, 1);
  g.fillPoints(shield, true);
  g.lineStyle(1.2, 0x550000, 1);
  g.strokePoints(shield, true);

  // Heraldic gold lion rampant
  g.fillStyle(0xf5c518, 1);

  // Haunches (lower-left, anchoring the rampant pose)
  g.fillEllipse(bx - 3.5, by + 3.5, 5, 3.5);

  // Torso (angled upper-right for rampant stance)
  g.fillEllipse(bx - 0.5, by + 1.5, 8, 4);

  // Neck
  g.fillEllipse(bx + 2.5, by - 0.5, 3.5, 4.5);

  // Head
  g.fillCircle(bx + 3, by - 3, 2.6);

  // Raised right foreleg (holds sword)
  g.fillEllipse(bx + 6.5, by - 0.5, 2.2, 5);

  // Lower left foreleg
  g.fillEllipse(bx + 1.5, by + 3.5, 2, 3.5);

  // Tail curling up-left
  g.lineStyle(2.2, 0xf5c518, 1);
  g.lineBetween(bx - 5, by + 2.5, bx - 8, by - 0.5);
  g.lineBetween(bx - 8, by - 0.5, bx - 6.5, by - 3.5);

  // Sword blade (upper-right from paw)
  g.lineStyle(1.8, 0xf5c518, 1);
  g.lineBetween(bx + 6.5, by - 1.5, bx + 8.5, by - 5.5);

  // Crossguard
  g.lineStyle(1.5, 0xf5c518, 1);
  g.lineBetween(bx + 5, by - 0.5, bx + 7.5, by - 1.5);
}

// Panzer Grenadier infantry: field-grey shield with a silver lightning bolt —
// motorised infantry, fast assault.
function drawPanzerGrenadierBadge(g: Phaser.GameObjects.Graphics, bx: number, by: number): void {
  const W = 9, H = 11;

  // Field-grey shield
  const shield = [
    { x: bx - W, y: by - H * 0.50 },
    { x: bx + W, y: by - H * 0.50 },
    { x: bx + W, y: by + H * 0.20 },
    { x: bx,     y: by + H * 0.56 },
    { x: bx - W, y: by + H * 0.20 },
  ];
  g.fillStyle(0x4c5765, 1);
  g.fillPoints(shield, true);
  g.lineStyle(1.2, 0x1e2530, 1);
  g.strokePoints(shield, true);

  // Lightning bolt — 8-point polygon, silver
  g.fillStyle(0xd8dce0, 1);
  g.fillPoints([
    { x: bx + 3.0, y: by - 5.5 },   // top right
    { x: bx + 0.5, y: by - 5.5 },   // top left
    { x: bx - 2.5, y: by + 0.0 },   // mid-left (step)
    { x: bx - 0.5, y: by + 0.0 },   // mid-notch
    { x: bx - 2.0, y: by + 5.5 },   // bottom left
    { x: bx + 0.5, y: by + 5.5 },   // bottom right
    { x: bx + 3.5, y: by + 0.0 },   // mid far-right
    { x: bx + 1.0, y: by + 0.0 },   // mid-right step
  ], true);
}

// Axis Special Forces armor: deep-purple heraldic shield with a black Stahlhelm
// (German steel helmet) silhouette — used for SF armor in both Operation Cobra
// and Mont Mouchet.
function drawPanzerSFBadge(g: Phaser.GameObjects.Graphics, bx: number, by: number): void {
  const W = 9, H = 11;

  // Deep-purple shield
  const shield = [
    { x: bx - W, y: by - H * 0.50 },
    { x: bx + W, y: by - H * 0.50 },
    { x: bx + W, y: by + H * 0.20 },
    { x: bx,     y: by + H * 0.56 },
    { x: bx - W, y: by + H * 0.20 },
  ];
  g.fillStyle(0x6b24a8, 1);
  g.fillPoints(shield, true);
  g.lineStyle(1.2, 0x350d60, 1);
  g.strokePoints(shield, true);

  // Stahlhelm (M35 profile) — black
  const hx = bx, hy = by + 0.5;
  g.fillStyle(0x111111, 1);

  // Dome — slightly wider than tall, flattened at top
  g.fillEllipse(hx, hy - 0.5, 13, 9);

  // Brim — wide flat band below the dome
  g.fillRect(hx - 7.5, hy + 2.5, 15, 2.2);

  // Small ventilation knob on top
  g.fillCircle(hx, hy - 5.2, 1.1);

  // Highlight to suggest the curved steel surface
  g.lineStyle(0.8, 0x888888, 0.5);
  g.lineBetween(hx - 3.5, hy - 3.5, hx, hy - 5.2);
}

// Lower-centre of the hex, just below the figure-count badge (at (0, TOKEN_R-5)).
// x=0 keeps it centred regardless of the horizontal mirror infantry sprites get
// (setScale(facesSouth ? -1 : 1, 1)) — so it always reads as "below the unit"
// for both players, whichever side's units they're looking at.
const VARIANT_BADGE_X = 0;
const VARIANT_BADGE_Y = 27;

function drawVariantBadge(g: Phaser.GameObjects.Graphics, variant: string): void {
  if (variant === 'commando')   drawCommandoBadge(g, VARIANT_BADGE_X, VARIANT_BADGE_Y);
  if (variant === 'airborne')   drawAirborneBadge(g, VARIANT_BADGE_X, VARIANT_BADGE_Y);
  if (variant === 'ranger')     drawRangerBadge(g, VARIANT_BADGE_X, VARIANT_BADGE_Y);
  if (variant === 'resistance') drawResistanceBadge(g, VARIANT_BADGE_X, VARIANT_BADGE_Y);
  if (variant === 'ski-troops') drawSkiTroopsBadge(g, VARIANT_BADGE_X, VARIANT_BADGE_Y);
  if (variant === 'panzer-sf')         drawPanzerSFBadge(g, VARIANT_BADGE_X, VARIANT_BADGE_Y);
  if (variant === 'panzer-grenadier') drawPanzerGrenadierBadge(g, VARIANT_BADGE_X, VARIANT_BADGE_Y);
}

// ── UnitSprite ────────────────────────────────────────────────────────────────

export class UnitSprite extends Phaser.GameObjects.Container {
  private gfx:        Phaser.GameObjects.Graphics;
  private outlineGfx: Phaser.GameObjects.Graphics;
  private figText:    Phaser.GameObjects.Text;
  private flipped:    boolean;
  private nation:     NationKey;
  unitId: string;

  constructor(scene: Phaser.Scene, data: UnitData, boardOffsetX: number, boardOffsetY: number, flipped: boolean, factions?: Factions) {
    const { x, y } = hexToPixelFlipped(data.col, data.row, boardOffsetX, boardOffsetY, flipped);
    super(scene, x, y);
    this.flipped = flipped;
    this.unitId  = data.id;
    this.nation  = (data.side === 'allies' ? factions?.allies : factions?.axis) ?? (data.side === 'allies' ? 'us' : 'german');

    this.gfx        = scene.add.graphics();
    this.outlineGfx = scene.add.graphics();

    // Small figure-count badge at bottom edge — quick reference during play
    this.figText = scene.add.text(0, TOKEN_R - 5, `${data.figures}`, {
      fontSize: '9px', color: '#ffffff', fontStyle: 'bold',
      shadow: { offsetX: 1, offsetY: 1, color: '#000', blur: 2, fill: true },
    }).setOrigin(0.5);

    this.drawUnit(data);
    this.deselect();

    // Idle bob — gfx only, so container position stays stable for hit-testing and move tweens
    const delay = ((data.col * 3 + data.row * 7) % 8) * 225;
    scene.tweens.add({
      targets: this.gfx,
      x: { from: -1, to: 1 },
      duration: 2500,
      ease: 'Sine.easeInOut',
      yoyo: true,
      repeat: -1,
      delay,
    });

    this.add([this.gfx, this.outlineGfx, this.figText]);
    this.setSize(TOKEN_R * 2, TOKEN_R * 2);
    this.setInteractive({ useHandCursor: true });
    scene.add.existing(this);
  }

  private drawUnit(data: UnitData): void {
    this.gfx.clear();
    const n = Math.max(1, Math.min(data.figures, 4));
    const facesSouth = (data.side === 'axis') !== this.flipped;
    if (data.type === 'infantry') {
      drawInfantryByNation(this.gfx, this.nation, n, facesSouth, data.variant);
    } else if (data.type === 'artillery') {
      drawArtilleryByNation(this.gfx, this.nation, data.figures);
      this.gfx.setScale(1, 1);
      this.gfx.setAngle(facesSouth ? 180 : 0);
    } else {
      drawArmorByNation(this.gfx, this.nation, data.figures);
      this.gfx.setScale(1, 1);
      this.gfx.setAngle(facesSouth ? 180 : 0);
    }
    if (data.variant) drawVariantBadge(this.gfx, data.variant);
  }

  update(data: UnitData, boardOffsetX: number, boardOffsetY: number, flipped: boolean, factions?: Factions): void {
    const { x, y } = hexToPixelFlipped(data.col, data.row, boardOffsetX, boardOffsetY, flipped);
    if (x !== this.x || y !== this.y) {
      this.scene.tweens.killTweensOf(this);
      this.scene.tweens.add({ targets: this, x, y, duration: 350, ease: 'Cubic.easeInOut' });
    }
    this.flipped = flipped;
    this.nation  = (data.side === 'allies' ? factions?.allies : factions?.axis) ?? (data.side === 'allies' ? 'us' : 'german');
    this.figText.setText(`${data.figures}`);
    this.drawUnit(data);
    this.gfx.setAlpha(data.hasAttacked ? 0.45 : 1.0);
  }

  select(): void {
    this.outlineGfx.clear();
    this.outlineGfx.lineStyle(3, 0xffff00, 1);
    this.outlineGfx.strokeCircle(0, 0, TOKEN_R);
  }

  deselect(): void {
    this.outlineGfx.clear();
  }
}
